import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import queryString from "querystring";
import { useClient, useAuth } from "./auth-context";
import { StaffPermissions } from "../types/staff";
import { useAdminStore } from "../store/adminStore";
import { School } from "../types/school";
import { useHistory } from "react-router-dom";
import { useToast } from "@chakra-ui/react";

export type PermissionData = {
  id: number;
  isCreateReview: boolean;
  isDoReview: boolean;
  isSameOrganization: boolean;
  isSeeAndEditInsight: boolean;
  isSeeDashboard: boolean;
  isSeeOwnReview: boolean;
  isStaffData: boolean;
  permission_institute: Array<PermissionInstitute>;
  mat: { id: string; title: string; matId: string };
};
export type PermissionInstitute = {
  id: number;
  institute: School;
  peopleId: string;
  role: string;
};
export function useStaffPermission(page: number, pageSize: number) {
  const client = useClient();
  const { data: user } = useAuth();
  const selectedSchool = useAdminStore(state => state.school);
  const matOnly = useAdminStore(state => state.matOnly);
  const managementFilters = useAdminStore(state => state.managementFilterState);
  const filters = useAdminStore(state => state.filterState);
  const dispatch = useAdminStore(state => state.changeManagementFilter);
  const changeFilter = useAdminStore(state => state.changeFilter);

  const instituteId =
    selectedSchool && matOnly === false
      ? selectedSchool.id
      : user?.institute_id;

  const [staffPermission, setStaffPermission] = useState<
    Array<StaffPermissions>
  >([]);

  const { data = [], refetch, isLoading, isPreviousData } = useQuery<
    Array<StaffPermissions>
  >(
    [
      "staffPermission",
      managementFilters.searchPermissionList,
      managementFilters.managementRole
    ],
    () => {
      const filtersQueryString = queryString.stringify({
        ...(managementFilters.managementRole
          ? { role: managementFilters.managementRole }
          : {}),

        ...(managementFilters.searchPermissionList
          ? { search: managementFilters.searchPermissionList }
          : {}),

        matOnly
      });
      return client(
        `staff-permission/${instituteId}?${filtersQueryString}`,
        {}
      );
    },

    {
      keepPreviousData: true
    }
  );

  useEffect(() => {
    refetch();
  }, [instituteId, matOnly, refetch, managementFilters.searchPermissionList]);

  useEffect(() => {
    setStaffPermission(data.slice(page * pageSize, (page + 1) * pageSize));
  }, [page, pageSize, data]);

  const totalPages = Math.ceil(data.length / pageSize);
  return {
    data: {
      staffPermissionList: staffPermission,
      hasMore: totalPages > page + 1,
      totalPages
    },
    allStaffPermissionData: data,
    isPermissionListLoading: isLoading,
    changeManagementFilter: dispatch,
    refetchStaffPermission: refetch,
    changeFilter,
    managementFilters,
    filters,
    isPreviousData,
    hasMore: totalPages > page + 1
  };
}

export const useCreatePermission = () => {
  const client = useClient();
  const history = useHistory();
  const { mutate, isLoading, isSuccess } = useMutation(
    "createPermission",
    (payload: any) => {
      return client(`permission/`, {
        method: "POST",
        data: payload
      });
    },
    {
      onSuccess: (_result, variables, _context) => {
        history.push("/staffmanagement/management_roles");
      }
    }
  );

  return {
    createPermission: mutate,
    isUpdating: isLoading,
    isSuccess: isSuccess
  };
};
export const useDeletePermission = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { mutate, isLoading, isSuccess } = useMutation(
    "deletePermission",
    ({
      permission_id,
      peopleId
    }: {
      permission_id: string;
      peopleId: string | undefined;
    }) => {
      const filtersQueryString = queryString.stringify({
        peopleId: peopleId
      });
      return client(`permission/${permission_id}?${filtersQueryString}`, {
        method: "DELETE"
      });
    },
    {
      onSuccess: (_result, variables, _context) => {
        queryClient.invalidateQueries(["staffPermission"]);
        queryClient.invalidateQueries(["staffList"]);
        toast({
          description: "User Role deleted successfully.",
          status: "success",
          duration: 3000,
          position: "bottom-right",
          isClosable: true
        });
      }
    }
  );

  return {
    deletePermission: mutate,
    isLoading: isLoading,
    isSuccess: isSuccess
  };
};

export const useUpdatePermission = () => {
  const client = useClient();

  const { mutate, isLoading, isSuccess } = useMutation(
    "updatePermission",
    (payload: any) => {
      return client(`update-permission`, {
        method: "POST",
        data: payload
      });
    }
  );

  return {
    updatePermission: mutate,
    isUpdating: isLoading,
    isSuccess: isSuccess
  };
};

export const useEditPermission = (permission_id: any) => {
  const client = useClient();
  const { mutate } = useMutation("editPermission", () => {
    return client(`update-permission-role/${permission_id}`, {
      method: "GET"
    });
  });

  return {
    editPermissionData: mutate
  };
};

export const useGetPermission = (permission_id: string | undefined) => {
  const client = useClient();

  const { data, isLoading } = useQuery(["getpermission", permission_id], () => {
    return client(`permission/${permission_id}`, {
      method: "GET"
    });
  });

  return {
    permissionData: data as PermissionData,
    isLoading: isLoading
  };
};
